import { AppBar, Backdrop, Box, Card, Container, Divider, Grid, styled, Toolbar, Typography } from "@mui/material";
import { type FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LoadingScreen, PageContainer, SearchTextField, TenantCard } from "@packsize/components";
import { ApplicationLogo, useAuthentication, useToken, useUser } from "@packsize/authentication";
import { TenantStatusName } from "@packsize/identity-api-sdk";

import { FailedRetry } from "@/components/failed-retry";
import { SiteCardDeleteButton } from "@/components/site-card/site-card-delete-button";
import { useTenants } from "@/hooks/tenant";
import { getLoginDestination } from "@/utils/get-login-destination";

const SITE_SELECT_SHOWN_KEY = "siteSelectShown";

export const CardGrid = styled(Grid)(({ theme }) => ({
	width: "100%",
	display: "grid",
	flex: 1,
	minHeight: 0,
	overflow: "auto",
	alignItems: "stretch",
	alignContent: "start",
	padding: theme.spacing(3),
	gap: theme.spacing(3),
	gridTemplateColumns: "repeat(2, 1fr)",
	[theme.breakpoints.down("xl")]: {
		gridTemplateColumns: "repeat(2, 1fr)",
	},
	[theme.breakpoints.down("lg")]: {
		gridTemplateColumns: "repeat(1, 1fr)",
	},
	gridAutoRows: "max-content",
	overflowY: "auto",
	overflowX: "hidden",
}));

export const SiteSelect: FC = () => {
	const intl = useIntl();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const { decodedToken } = useToken();
	const { currentUser } = useUser();
	const { loginToSite } = useAuthentication();

	const { data: tenants, error, refetch, isFetching } = useTenants();

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		sessionStorage.setItem(SITE_SELECT_SHOWN_KEY, "true");
	}, []);

	const handleLogin = useCallback(
		async (tenantId: string) => {
			setLoading(true);

			const result = await loginToSite(tenantId);

			if (result === null) {
				setLoading(false);
				return;
			}

			navigate(getLoginDestination(result.user, result.decodedToken, searchParams.get("returnTo")));
		},
		[loginToSite, navigate, searchParams],
	);

	useEffect(() => {
		const tenantId = searchParams.get("tenantId");

		if (!tenantId) {
			return;
		}

		handleLogin(tenantId);
	}, [handleLogin, searchParams]);

	const tenantsToDisplay = tenants
		.filter((tenant) => tenant.tenantStatus?.name !== TenantStatusName.Decommissioned)
		.filter((tenant) => {
			return (
				tenant.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
				tenant.siteId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				tenant.salesForceId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				tenant.siteTimeZone?.toLowerCase().includes(searchTerm.toLowerCase())
			);
		})
		.sort((firstTenant, secondTenant) => {
			const firstIsActive = firstTenant.id === decodedToken?.tid;
			const secondIsActive = secondTenant.id === decodedToken?.tid;

			if (firstIsActive !== secondIsActive) {
				return firstIsActive ? -1 : 1;
			}

			return firstTenant.company.localeCompare(secondTenant.company);
		});
	const primaryTenant = tenants.find((tenant) => tenant.id === currentUser?.tenantId);

	const handleBack = useCallback(() => navigate("/login"), [navigate]);
	if (loading) {
		return <LoadingScreen />;
	}

	if (error) {
		return (
			<Backdrop open sx={{ backgroundColor: "background.default" }}>
				<Container maxWidth="xl" sx={{ display: "flex", height: "calc(100vh - 128px)" }}>
					<PageContainer sx={{ flex: 1, borderRadius: 1, justifyContent: "center" }}>
						<FailedRetry retry={refetch} />
					</PageContainer>
				</Container>
			</Backdrop>
		);
	}

	return (
		<Grid
			container
			sx={{
				height: "100%",
				display: "grid",
				gridTemplateRows: "64px calc(100vh - 64px)",
				gridTemplateAreas: `"header" "main"`,
			}}
		>
			<AppBar sx={{ gridArea: "header" }}>
				<Toolbar sx={{ justifyContent: "space-between" }}>
					<Box sx={{ "& svg": { width: "180px" } }}>
						<ApplicationLogo />
					</Box>
				</Toolbar>
			</AppBar>

			<PageContainer sx={{ gridArea: "main" }}>
				<Container maxWidth="xl" sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
					<Grid container display="grid" gridTemplateColumns="1fr auto" alignItems="center" sx={{ mb: 4 }}>
						<Box
							sx={{ display: "flex", alignItems: "center", width: "fit-content", cursor: "pointer" }}
							role="button"
							tabIndex={0}
							data-testid="site-select-back"
							onClick={handleBack}
							onKeyDown={(event) => {
								if (event.key === "Enter" || event.key === " ") {
									handleBack();
								}
							}}
						>
							<ChevronLeftIcon />
							<Typography>{decodedToken?.tname ?? <FormattedMessage id="Back" defaultMessage="Back" />}</Typography>
						</Box>

						<Typography variant="caption" color="info">
							{currentUser?.userName}
						</Typography>
					</Grid>

					<Card sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
						<Grid container display="grid" gridTemplateColumns="1fr auto" alignItems="center" sx={{ p: 4 }}>
							<Typography variant="h1" whiteSpace="nowrap">
								<FormattedMessage id="My Sites" defaultMessage="My Sites" />
							</Typography>

							{primaryTenant ? (
								<Typography variant="caption">
									<FormattedMessage
										id="Primary: {site}"
										defaultMessage="Primary: {site}"
										values={{ site: primaryTenant.company }}
									/>
								</Typography>
							) : null}
						</Grid>

						<Divider />

						<Box display="flex" flexDirection="column" sx={{ px: 3, pt: 3 }} gap={1}>
							<SearchTextField
								value={searchTerm}
								onChange={(value) => setSearchTerm(value)}
								placeholder={intl.formatMessage({
									id: "Search by site name...",
									defaultMessage: "Search by site name...",
								})}
								sx={{ width: "100%", backgroundColor: "common.white" }}
							/>
							<Typography variant="caption">
								<FormattedMessage
									id="Showing {shown} of {total} sites"
									defaultMessage="Showing {shown} of {total} sites"
									values={{ shown: tenantsToDisplay.length, total: tenants.length }}
								/>
							</Typography>
						</Box>

						<CardGrid>
							{isFetching === true && tenantsToDisplay.length === 0 && (
								<Box
									sx={{
										gridColumn: "1 / -1",
										minHeight: "100%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<FormattedMessage id="Fetching your sites..." defaultMessage="Fetching your sites..." />
								</Box>
							)}

							{isFetching === false && tenantsToDisplay.length === 0 && (
								<Box
									sx={{
										gridColumn: "1 / -1",
										minHeight: "100%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<FormattedMessage id="No sites configured" defaultMessage="No sites configured" />
								</Box>
							)}

							{tenantsToDisplay.length > 0 &&
								tenantsToDisplay.map((tenant) => (
									<TenantCard
										key={tenant.id}
										tenant={tenant}
										tenantStatus={tenant.tenantStatus?.name ?? TenantStatusName.Internal}
										currentUserId={decodedToken?.tid}
										tenantcardActions={
											currentUser?.tenantId !== tenant.id &&
											tenant.expiresAt !== null &&
											new Date(tenant.expiresAt) > new Date() ? (
												<SiteCardDeleteButton site={tenant} />
											) : undefined
										}
										onClick={(selectedTenant) => handleLogin(selectedTenant.id)}
									/>
								))}
						</CardGrid>
					</Card>
				</Container>
			</PageContainer>
		</Grid>
	);
};
